import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import Modal from '../shared/Modal'

const EMPTY_PREFERENCES = {}

const VariablesExportModal = ({
  onClose, onExport, variables, collect, startingAt,
  savedPreferences = EMPTY_PREFERENCES
}) => {
  const initializeSelection = () => {
    if (savedPreferences.variable_ids && savedPreferences.variable_ids.length > 0) {
      const validVariableIds = new Set(variables.map(variable => variable.id.toString()))
      return savedPreferences.variable_ids
        .filter(id => validVariableIds.has(id.toString()))
        .map(id => parseInt(id, 10))
    }
    return variables.map(variable => variable.id)
  }

  const [selectedVariables, setSelectedVariables] = useState(initializeSelection())

  const ensemble = collect.ensembles.find(esbl => esbl.id === collect.ensemble_id)
  const [searchTerm, setSearchTerm] = useState('')
  const hasAllVariablesPref = savedPreferences.all_variables !== null &&
    typeof savedPreferences.all_variables !== 'undefined'
  const [withVariables, setWithVariables] = useState(
    hasAllVariablesPref ? !savedPreferences.all_variables : false
  )

  const handleSubmit = useCallback(event => {
    event.preventDefault()
    const selectedPayload = { variableIds: selectedVariables, allVariables: false }
    const allPayload = { variableIds: [], allVariables: true }
    const variablesPayload = withVariables ? selectedPayload : allPayload

    onExport({
      ...variablesPayload
    })
  }, [onExport, selectedVariables, withVariables])

  const handleSelectAll = useCallback(() => {
    setSelectedVariables(variables.map(variable => variable.id))
  }, [variables])

  const handleDeselectAll = useCallback(() => {
    setSelectedVariables([])
  }, [])

  const handleVariableToggle = useCallback(variableId => {
    setSelectedVariables(prev => {
      if (prev.includes(variableId))
        return prev.filter(id => id !== variableId)

      return [...prev, variableId]
    })
  }, [])

  const handleSearchChange = useCallback(event => {
    setSearchTerm(event.target.value)
  }, [])

  const handleWithVariablesChange = useCallback(() => {
    setWithVariables(prev => !prev)
  }, [])

  const filteredVariables = variables.filter(variable =>
    variable.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const groupedVariables = filteredVariables
    .sort((item1, item2) => item1.name.toLowerCase().localeCompare(item2.name.toLowerCase()))
    .reduce((acc, variable) => {
      const firstLetter = variable.name[0].toUpperCase()
      if (!acc[firstLetter])
        acc[firstLetter] = []

      acc[firstLetter].push(variable)
      return acc
    }, {})

  const handleKeyPress = useCallback(variableId => event => {
    if (event.key === 'Enter' || event.key === ' ')
      handleVariableToggle(variableId)
  }, [handleVariableToggle])

  const handleVariableClick = useCallback(variableId => () => {
    handleVariableToggle(variableId)
  }, [handleVariableToggle])

  const modalTitle = (
    <>
      <i className="fas fa-share-square" />
      <span className="ml-2">
        Personnaliser l&apos;export Excel
      </span>
    </>
  )

  return (
    <Modal
      title={modalTitle}
      footer={
        <div className="d-flex justify-content-end align-items-center w-100">
          <button
            type="button"
            className="btn btn-light mr-2"
            onClick={onClose}
          >
            Fermer
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={withVariables ? selectedVariables.length === 0 : null}
            onClick={handleSubmit}
          >
            Exporter
          </button>
        </div>
      }
      isOpen
      onClose={onClose}
    >
      <div className="d-flex flex-column">
        <div className="mb-4 bg-white border p-3">
          <p className="mb-2">
            <strong>Collecte :</strong>
            {' '}
            {collect.name}
            {startingAt ? (
              <span>
                {' '}
                (planification du
                {' '}
                {startingAt}
                )
              </span>
            ) : null}
          </p>
          <p className="mb-0">
            <strong>Ensemble :</strong>
            {' '}
            {ensemble.name}
          </p>
        </div>
        <div className="custom-control custom-switch mb-3">
          <input
            type="checkbox"
            className="custom-control-input"
            id="variables-toggle"
            checked={withVariables}
            onChange={handleWithVariablesChange}
          />
          <label className="custom-control-label font-weight-bolder" htmlFor="variables-toggle">
            Personnaliser l&apos;export des variables de l&apos;ensemble
          </label>
        </div>
        {withVariables ? (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <h6 className="mb-0 font-weight-bolder">
                  Variables sélectionnées (
                  {selectedVariables.length}
                  )
                </h6>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-link p-0 mr-2"
                  onClick={handleSelectAll}
                >
                  Tout cocher
                </button>
                {' / '}
                <button
                  type="button"
                  className="btn btn-link p-0 ml-2"
                  onClick={handleDeselectAll}
                >
                  Tout décocher
                </button>
              </div>
            </div>
            <div className="form-group">
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text bg-light border border-right-0 rounded-left">
                    <i className="fas fa-search text-secondary" />
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control bg-light border rounded-right border-left-0 pl-0"
                  placeholder="Rechercher"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="variables-list flex-grow-1 overflow-auto" style={{ height: '40vh' }}>
              {Object.keys(groupedVariables).length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p className="mb-0">Aucune variable</p>
                </div>
              ) : Object.entries(groupedVariables)
                .sort(([letter1], [letter2]) => letter1.localeCompare(letter2))
                .map(([letter, vars]) => (
                  <div key={letter} className="mb-3">
                    <h6 className="font-weight-bolder m-0">{letter}</h6>
                    {vars.map(variable => (
                      <div
                        key={variable.id}
                        className="d-flex align-items-center py-2"
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={handleVariableClick(variable.id)}
                        onKeyPress={handleKeyPress(variable.id)}
                      >
                        <div className="custom-control custom-checkbox flex-grow-1">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`variable-${variable.id}`}
                            checked={selectedVariables.includes(variable.id)}
                            onChange={handleVariableClick(variable.id)}
                          />
                          <label
                            className="custom-control-label text-body user-select-none"
                            htmlFor={`variable-${variable.id}`}
                          >
                            {variable.name}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

VariablesExportModal.propTypes = {
  collect: PropTypes.shape({
    ensembles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
      })
    ).isRequired,
    // eslint-disable-next-line camelcase
    ensemble_id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    // eslint-disable-next-line camelcase
    planification_date: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired
}

export default VariablesExportModal